<!-- CarePlan_EditModule_Modal.vue -->
<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="fixed inset-0 bg-gray-800/60" @click="$emit('close')"></div>

      <div class="bg-white rounded-xl shadow-xl border border-gray-200 max-w-4xl w-full relative z-10 max-h-[90vh] overflow-hidden flex flex-col">
        <div class="px-6 py-4 border-b border-gray-200 bg-primary-100">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-sm font-semibold text-gray-900">Edit Module</h3>
              <p v-if="threadType" class="text-xs text-gray-600 mt-0.5">
                Domain: {{ threadType.name }}
                <span v-if="moduleTemplate"> · Template: {{ moduleTemplate.name }}</span>
              </p>
            </div>
            <button @click="$emit('close')" class="text-gray-600 hover:text-gray-900">
              <FontAwesomeIcon :icon="['fas', 'xmark']" />
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-5">
          <div class="space-y-6">
            <!-- Module Title -->
            <div>
              <label class="block text-sm font-medium mb-1">
                Module Title <span class="text-red-500">*</span>
              </label>
              <input
                v-model="editData.title"
                type="text"
                class="w-full border rounded px-3 py-2"
                placeholder="Enter module title"
              >
            </div>

            <!-- Status -->
            <div>
              <label class="block text-sm font-medium mb-1">Status</label>
              <select v-model="editData.status" class="w-full border rounded px-3 py-2">
                <option
                  v-for="status in allowedStatuses"
                  :key="status"
                  :value="status"
                >
                  {{ formatStatus(status) }}
                </option>
              </select>
            </div>

            <!-- Module Fields (if schema exists) -->
            <div v-if="fieldSchema && fieldSchema.length > 0" class="space-y-4">
              <h4 class="text-sm font-semibold text-gray-700">Module Information</h4>
              <div v-for="field in sortedFields" :key="field.id">
                <SurveyFieldRenderer
                  :field="field"
                  v-model="editData.field_values[field.id]"
                  @validate="validateField(field.id)"
                />
              </div>
            </div>

            <!-- Goals -->
            <div>
              <div class="flex justify-between items-center mb-2">
                <label class="text-sm font-medium">Goals</label>
                <button
                  @click="addGoal"
                  type="button"
                  class="text-sm text-primary-600 hover:text-primary-700"
                >
                  <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />
                  Add Goal
                </button>
              </div>
              <div class="space-y-2">
                <div
                  v-for="(goal, i) in editData.goals"
                  :key="goal.id || i"
                  class="bg-gray-50 p-3 rounded"
                >
                  <input
                    v-model="goal.title"
                    placeholder="Goal title"
                    class="w-full border rounded px-2 py-1 mb-2"
                  >
                  <div class="flex items-center justify-between">
                    <select
                      v-model="goal.status"
                      class="text-sm border rounded px-2 py-1"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="blocked">Blocked</option>
                    </select>
                    <button
                      @click="removeGoal(i)"
                      type="button"
                      class="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                  <div v-if="goal.measurement_type" class="text-xs text-gray-500 mt-1">
                    Type: {{ goal.measurement_type }}
                    <span v-if="goal.target_value"> • Target: {{ goal.target_value }}</span>
                  </div>
                </div>
                <div v-if="editData.goals.length === 0" class="text-sm text-gray-500">
                  No goals defined
                </div>
              </div>
            </div>

            <!-- Interventions -->
            <div>
              <div class="flex justify-between items-center mb-2">
                <label class="text-sm font-medium">Interventions</label>
                <button
                  @click="addIntervention"
                  type="button"
                  class="text-sm text-primary-600 hover:text-primary-700"
                >
                  <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />
                  Add Intervention
                </button>
              </div>
              <div class="space-y-2">
                <div
                  v-for="(intervention, i) in editData.interventions"
                  :key="intervention.id || i"
                  class="bg-gray-50 p-3 rounded"
                >
                  <input
                    v-model="intervention.title"
                    placeholder="Intervention"
                    class="w-full border rounded px-2 py-1 mb-2"
                  >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                      <select
                        v-model="intervention.status"
                        class="text-sm border rounded px-2 py-1"
                      >
                        <option value="planned">Planned</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="discontinued">Discontinued</option>
                      </select>
                      <input
                        v-model="intervention.frequency"
                        placeholder="Frequency"
                        class="text-sm border rounded px-2 py-1 w-32"
                      >
                    </div>
                    <button
                      @click="removeIntervention(i)"
                      type="button"
                      class="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div v-if="editData.interventions.length === 0" class="text-sm text-gray-500">
                  No interventions defined
                </div>
              </div>
            </div>

            <!-- Notes Section -->
            <div>
              <label class="block text-sm font-medium mb-1">Notes</label>
              <textarea
                v-model="editData.notes"
                rows="3"
                class="w-full border rounded px-3 py-2"
                placeholder="Additional notes about this module..."
              ></textarea>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div class="text-xs text-gray-400">
            <span v-if="module.created_at">Created: {{ formatDate(module.created_at) }}</span>
            <span v-if="module.updated_at" class="ml-3">Updated: {{ formatDate(module.updated_at) }}</span>
          </div>
          <div class="flex space-x-3">
            <button
              @click="$emit('close')"
              type="button"
              class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              @click="save"
              type="button"
              :disabled="!isValid"
              class="btn-primary"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SurveyFieldRenderer from '@/components/widgetcomponents/JSON_Editors/SurveyFieldRenderer.vue';

export default {
  components: {
    FontAwesomeIcon,
    SurveyFieldRenderer
  },

  props: {
    module: {
      type: Object,
      required: true
    },
    threadType: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      editData: {
        title: '',
        status: 'active',
        field_values: {},
        goals: [],
        interventions: [],
        notes: ''
      },
      fieldErrors: {}
    };
  },

  computed: {
    domainConfig() {
      return this.threadType?.metadata?.domain_config || {};
    },

    allowedStatuses() {
      return this.domainConfig.allowed_statuses || ['planned', 'active', 'on_hold', 'completed', 'discontinued'];
    },

    moduleTemplate() {
      if (!this.module.metadata?.template_used || !this.threadType?.metadata?.module_templates) {
        return null;
      }
      return this.threadType.metadata.module_templates.find(
        t => t.template_id === this.module.metadata.template_used
      );
    },

    fieldSchema() {
      // First check if the module has its own field schema stored
      if (this.module.metadata?.field_schema?.fields) {
        return this.module.metadata.field_schema.fields;
      }
      // Otherwise get from the template if it was used
      if (this.moduleTemplate?.fields) {
        return this.moduleTemplate.fields;
      }
      return null;
    },

    sortedFields() {
      if (!this.fieldSchema) return [];
      return [...this.fieldSchema].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });
    },

    isValid() {
      if (!this.editData.title || this.editData.title.trim() === '') {
        return false;
      }

      // Check required fields if schema exists
      if (this.fieldSchema) {
        const requiredFields = this.fieldSchema.filter(f => f.required);
        for (const field of requiredFields) {
          const value = this.editData.field_values[field.id];
          if (!this.hasValue(value)) {
            return false;
          }
        }
      }

      return true;
    }
  },

  mounted() {
    // Deep clone the module metadata to avoid direct mutations
    this.editData = {
      title: this.module.title || this.module.metadata?.title || '',
      status: this.module.metadata?.status || 'active',
      field_values: { ...(this.module.metadata?.field_values || {}) },
      goals: JSON.parse(JSON.stringify(this.module.metadata?.goals || [])),
      interventions: JSON.parse(JSON.stringify(this.module.metadata?.interventions || [])),
      notes: this.module.metadata?.notes || ''
    };

    // Initialize field values for any missing fields
    if (this.fieldSchema) {
      this.fieldSchema.forEach(field => {
        if (!(field.id in this.editData.field_values)) {
          if (field.type === 'checkbox') {
            this.editData.field_values[field.id] = [];
          } else if (field.type === 'boolean') {
            this.editData.field_values[field.id] = null;
          } else {
            this.editData.field_values[field.id] = '';
          }
        }
      });
    }
  },

  methods: {
    hasValue(value) {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    },

    validateField(fieldId) {
      // Placeholder for field validation if needed
    },

    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    },

    addGoal() {
      this.editData.goals.push({
        id: Date.now(),
        title: '',
        status: 'in_progress'
      });
    },

    removeGoal(index) {
      this.editData.goals.splice(index, 1);
    },

    addIntervention() {
      this.editData.interventions.push({
        id: Date.now(),
        title: '',
        status: 'planned',
        frequency: ''
      });
    },

    removeIntervention(index) {
      this.editData.interventions.splice(index, 1);
    },

    async save() {
      if (!this.isValid) {
        this.$emit('show-message', 'Please fill in all required fields', 'error');
        return;
      }

      try {
        // Prepare the updated metadata
        const updatedMetadata = {
          ...this.module.metadata,
          title: this.editData.title,
          status: this.editData.status,
          field_values: this.editData.field_values,
          goals: this.editData.goals,
          interventions: this.editData.interventions,
          notes: this.editData.notes,
          updated_at: new Date().toISOString()
        };

        // UPDATED: Use CarePlanThread endpoint
        const response = await this.$axios.patch(
          `api/careplanthreads/${this.module.id}/`,
          {
            title: this.editData.title,
            metadata: updatedMetadata
          }
        );

        if (response.data) {
          this.$emit('updated');
          this.$emit('show-message', 'Module updated successfully', 'success');
        }
      } catch (error) {
        console.error('Error updating module:', error);
        const errorMessage = error.response?.data?.error || 'Failed to update module';
        this.$emit('show-message', errorMessage, 'error');
      }
    }
  }
};
</script>
